import React, { ReactText, useState, useEffect } from "react";
import { Row, Col } from "antd";
import { ColumnType } from "antd/lib/table";
import { TableRowSelection } from "antd/lib/table/interface";
import ZButton from "@atoms/ZButton";
import ZModal from "@atoms/ZModal";
import ZDrawer from "@molecules/ZDrawer";
import ZTable from "@organisms/ZTable";
import usePrevious from "@/Hooks/usePrevious";
import { Product } from "@/Contracts/Products";
import { QuoteProduct } from "@/Contracts/ProposalBuilder/Block";
import { IUpdateMandatoryFieldsForQuoteBlockDrawer } from "./interface";
import "./index.less";

const ZUpdateMandatoryFieldsForQuoteBlockDrawer = <
	T extends Product | QuoteProduct = Product
>({
	visible,
	onClose,
	products,
	unfilledProducts,
	removeProducts,
	renderProductColumn,
	children
}: React.PropsWithChildren<
	IUpdateMandatoryFieldsForQuoteBlockDrawer<T>
>): React.ReactElement => {
	const removeSelectedProducts = (productsToBeRemoved: T[]) => {
		ZModal.confirm({
			title: "Are you sure you want to remove these items?",
			cancelText: "No",
			okText: "Yes",
			onOk: () => removeProducts(productsToBeRemoved)
		});
	};
	const [selectedProductList, setSelectedProductList] = useState<T[]>([]);
	const [productUpdatedId, setProductUpdatedId] = useState<string>("");
	const productColumns: ColumnType<T>[] = [
		{
			dataIndex: "name",
			key: "name",
			title: (
				<div className="flex items-center">
					<div>Select All</div>
					{selectedProductList.length > 0 && (
						<ZButton
							onClick={() =>
								removeSelectedProducts(selectedProductList)
							}
							size="small"
							className="ml-3"
							danger
							ghost
						>
							Remove Products
						</ZButton>
					)}
				</div>
			),
			render: (_text: unknown, record: T) =>
				record.id === productUpdatedId ? (
					<div className="remove-updated-product">
						{renderProductColumn(_text, record)}
					</div>
				) : (
					renderProductColumn(_text, record)
				)
		}
	];
	const productRowSelection: TableRowSelection<T> = {
		onChange: (
			_selectedProductIdList: ReactText[],
			selectedProductList: T[]
		) => {
			setSelectedProductList(selectedProductList);
		},
		type: "checkbox",
		columnWidth: "20",
		selectedRowKeys: selectedProductList.map(product => product.id ?? "")
	};
	const onDrawerClose = () => {
		setSelectedProductList([]);
		onClose();
	};
	const handleProductRowEvent = (record: T) => ({
		onClick: () =>
			setSelectedProductList(
				selectedProductList.some(product => product.id === record.id)
					? selectedProductList.filter(
							product => product.id !== record.id
					  )
					: [...selectedProductList, record]
			)
	});
	const prevProducts = usePrevious<T[]>(products);
	const prevUnfilledProducts = usePrevious<T[]>(unfilledProducts);
	if (
		products.length === prevProducts?.length &&
		unfilledProducts.length + 1 === prevUnfilledProducts?.length &&
		!productUpdatedId
	) {
		const updatedProduct = prevUnfilledProducts.find(
			prevUnfilledProduct =>
				!unfilledProducts.some(
					unfilledProduct =>
						prevUnfilledProduct.id === unfilledProduct.id
				)
		);
		setProductUpdatedId(updatedProduct?.id ?? "");
		setTimeout(() => setProductUpdatedId(""), 1500);
	}
	const renderRow = (rowProps: any) => {
		return (
			<tr
				{...rowProps}
				className={`${rowProps.className} ${
					productUpdatedId === rowProps["data-row-key"]
						? "bg-green-1"
						: ""
				}`}
			/>
		);
	};

	useEffect(() => {
		if (visible) {
			if (productUpdatedId && prevUnfilledProducts) {
				const newSelectedProductList = selectedProductList.filter(
					product =>
						prevUnfilledProducts?.find(
							unfilledProduct => unfilledProduct.id === product.id
						)
				);
				setSelectedProductList(newSelectedProductList);
			} else {
				const newSelectedProductList = selectedProductList.filter(
					product =>
						unfilledProducts?.find(
							unfilledProduct => unfilledProduct.id === product.id
						)
				);
				setSelectedProductList(newSelectedProductList);
			}
		}
	}, [prevUnfilledProducts, unfilledProducts]);

	return (
		<ZDrawer
			visible={visible}
			closable={true}
			destroyOnClose={true}
			onClose={onDrawerClose}
			className="missing-fields-drawer document-product-drawer search-document-product-drawer zomentum-drawer"
			title={
				<Row>
					<Col span={24}>
						<div className="drawer-title-container">
							<div className="drawer-title">
								Mandatory fields missing for below products.
								Update to continue
							</div>
						</div>
					</Col>
				</Row>
			}
			width={356}
		>
			<Row className="drawer-form-container no-padding">
				<Col
					span={24}
					className="table-page-container product-table-container"
				>
					<ZTable<T>
						showHeader={true}
						loading={false}
						size="small"
						dataSource={
							productUpdatedId
								? prevUnfilledProducts ?? []
								: unfilledProducts
						}
						columns={productColumns}
						rowKey="id"
						pagination={false}
						rowSelection={productRowSelection}
						onRow={handleProductRowEvent}
						className="product-table"
						components={{ body: { row: renderRow } }}
						isTopBorder={false}
					/>
				</Col>
			</Row>
			<div className="ant-drawer-footer">
				<ZButton
					type="default"
					htmlType="button"
					onClick={onDrawerClose}
					className="mr-4"
				>
					Cancel
				</ZButton>
				<ZButton
					type="primary"
					ghost
					htmlType="button"
					onClick={() => removeSelectedProducts(unfilledProducts)}
				>
					Skip above Products
				</ZButton>
			</div>
			{children}
		</ZDrawer>
	);
};
export default ZUpdateMandatoryFieldsForQuoteBlockDrawer;
